import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

interface Prediction {
  timestamps: string[];
  y_pred: number[];
  lower_95: number[];
  upper_95: number[];
  confidence: number[];
}

interface TimeSeriesPredictionChartProps {
  prediction: Prediction | null;
  loading?: boolean;
}

const TimeSeriesPredictionChart: React.FC<TimeSeriesPredictionChartProps> = ({ prediction, loading }) => {
  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-pulse bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 w-full h-full rounded-xl"></div>
      </div>
    );
  }

  const chartData = useMemo(() => {
    if (!prediction) return [];
    return prediction.timestamps.map((timestamp, index) => ({
      time: new Date(timestamp).toLocaleTimeString(),
      predicted: Math.round(prediction.y_pred[index] * 10) / 10,
      lower: Math.round(prediction.lower_95[index] * 10) / 10,
      upper: Math.round(prediction.upper_95[index] * 10) / 10,
      confidence: Math.round(prediction.confidence[index] * 100)
    }));
  }, [prediction]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-800/95 border border-slate-600/50 rounded-xl p-4 shadow-xl backdrop-blur-sm">
          <p className="text-slate-200 font-semibold mb-2">{label}</p>
          <p className="text-violet-400 font-bold">Predicted: {data.predicted} kW</p>
          <p className="text-blue-400">Range: {data.lower} - {data.upper} kW</p>
          <p className="text-green-400">Confidence: {data.confidence}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-64 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" opacity={0.3} />
          <XAxis 
            dataKey="time" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#64748b' }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#64748b' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="upper"
            stackId="1"
            stroke="none"
            fill="rgba(99, 102, 241, 0.1)"
          />
          <Area
            type="monotone"
            dataKey="lower"
            stackId="1"
            stroke="none"
            fill="rgba(255, 255, 255, 1)"
          />
          <Line
            type="monotone"
            dataKey="predicted"
            stroke="#6366f1"
            strokeWidth={3}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TimeSeriesPredictionChart;